import { useCallback, useReducer } from 'react'
import { useLocalStorage } from 'react-use'
import Bugsnag from '../utils/bugsnag'
import {
  getBlankStorageLadder,
  getBlankStorageTier,
  isFullStorageTier,
  sortTier,
} from './storage'
import { StorageLadder, StorageTier } from '../types'

export const LOCAL_STORAGE_KEY = 'ladder'
const UNSET_ACTIVE_TIER_INDEX = -1
const BLANK_LADDER = getBlankStorageLadder()

export interface State extends StorageLadder {
  activeTierIndex: number
}
export type Action =
  | { type: 'add_to_active_tier'; playerId: string }
  | { type: 'clear_tier'; index: number }
  | { type: 'remove_player'; playerId: string; tierIndex: number }
  | { type: 'reset' }
  | { type: 'set_active_tier'; index: number }
  | { type: 'unset_active_tier' }

const init = (initialLadder?: StorageLadder): State => ({
  ...BLANK_LADDER,
  activeTierIndex: UNSET_ACTIVE_TIER_INDEX,

  ...initialLadder,
})

const replaceTier = (
  tiers: StorageTier[],
  tier: StorageTier,
  index: number,
): StorageTier[] => [
  ...tiers.slice(0, index),
  sortTier(tier),
  ...tiers.slice(index + 1),
]

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'add_to_active_tier': {
      const { tiers, activeTierIndex } = state
      const { playerId } = action

      if (activeTierIndex < 0) {
        Bugsnag.notify(
          new Error(
            'There is no active tier index, cannot add another player.',
          ),
          (event) => {
            event.addMetadata('state', { state, action })
          },
        )
        return state
      }

      const tier = tiers[activeTierIndex]

      if (isFullStorageTier(tier)) {
        Bugsnag.notify(
          new Error(
            `Tier ${activeTierIndex + 1} is full, cannot add another player.`,
          ),
          (event) => {
            event.addMetadata('state', { state, action })
          },
        )
        return state
      }

      // add the player to the end and then sort it by ID,
      // (putting empty values at the end)
      const lastIndex = tier.length - 1
      const newTier = tier.map((id, index) =>
        index === lastIndex ? playerId : id,
      )

      return {
        ...state,
        tiers: replaceTier(tiers, newTier, activeTierIndex),

        // if the tier is now full w/ the addition of the player,
        // close the drawer. otherwise, leave it open so more players
        // can be quickly added
        activeTierIndex: isFullStorageTier(newTier) ? -1 : activeTierIndex,
      }
    }
    case 'clear_tier': {
      const { tiers } = state
      const { index: indexToClear } = action

      return {
        ...state,
        activeTierIndex: -1,
        tiers: replaceTier(
          tiers,
          getBlankStorageTier(indexToClear),
          indexToClear,
        ),
      }
    }
    case 'remove_player': {
      const { tiers } = state
      const { tierIndex, playerId } = action
      const tier = tiers[tierIndex]

      // remove player by keeping all the IDs and replacing the
      // match w/ empty
      const newTier = tier.map((id) => (id === playerId ? '' : id))

      return {
        ...state,
        activeTierIndex: -1,
        tiers: replaceTier(tiers, newTier, tierIndex),
      }
    }
    case 'reset':
      return init()
    case 'set_active_tier':
      return { ...state, activeTierIndex: action.index }
    case 'unset_active_tier':
      return { ...state, activeTierIndex: UNSET_ACTIVE_TIER_INDEX }
  }
}

export const getLadderFromState = (state: State): StorageLadder => {
  const { from, tiers } = state

  return { from, tiers }
}

export const usePersistReducer = () => {
  const [savedValue, saveValue] = useLocalStorage(
    LOCAL_STORAGE_KEY,
    BLANK_LADDER,
  )
  const reducerLocalStorage = useCallback(
    (state: State, action: Action): State => {
      const newState = reducer(state, action)

      saveValue(getLadderFromState(newState))

      return newState
    },
    [saveValue],
  )

  return useReducer(reducerLocalStorage, savedValue, init)
}
